import { Divider, Card, Col, Row, Space, Table, Tabs, Typography, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { coreApi } from "../api/calls";
import BuildingDetails from "../components/Buildings/BuildingDetails";
import BuildingUnitAddForm from "../components/Buildings/BuildingUnitAddForm";
import BuildingUnitContactPane from "../components/Buildings/BuildingUnitContactPane";
import BuildingInvoicePane from "../components/Buildings/BuildingInvoicePane";
import BuildingUnitsActiveCleanings from "../components/Buildings/BuildingUnitsActiveCleanings";
import RecordActionsBar from "../components/RecordActionsBar";
import CustomTabs from "../components/custom/CustomTabs";
import { BankOutlined, InfoCircleOutlined } from "@ant-design/icons";
import ContactsPane from "../components/ContactsPane";
import PartnerLink from "../components/Partners/PartnerLink";
import CommunicationPane from "../components/CommunicationsPane";
import ClaimsPane from "../components/Claims/ClaimsPane";
import CleaningListComplex from "../components/Cleanings/CleaningListComplex";
import { ParseRegularityFromRruleDays } from "../helpers";
import {SlideDown} from "react-slidedown";
import ScheduleMassTerminate from "../components/Scheduler/MultiStepEdit/ScheduleMultiStepMassEdit";
import moment from "moment/moment";
import {showActiveDays} from "../components/helpers/schedule";

// Gutter for <Row>
const gutter = [16, 16];

/**
 * Page with detail of building
 * @component
 * @alias BuildingProfile
 * @returns <Card /> with content
 */
const BuildingProfile = (props) => {
  const { t } = useTranslation();

  // States
  const [isLoading, setLoading] = useState(false);
  const [building, setBuilding] = useState({});
  const [redirectState, setRedirectState] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedBuildingUnits, setSelectedBuildingUnits] = useState([]);
  const [buildingUnits, setBuildingUnits] = useState();

  const { TabPane } = Tabs;

  const selectRrules = (buildingUnitIds) => {
    setSelectedBuildingUnits(
      buildingUnits.filter((buildingUnit) => {
        return buildingUnitIds.includes(buildingUnit?.id);
      })
    );
  };

  useEffect(() => {
    setLoading(true);

    coreApi
      .get("buildings/" + props.match.params.id)
      .then((res) => {
        setBuilding(res.data);
        setBuildingUnits(res.data.building_units);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.match.params.id]);

  const renderDocumentDate = (date) => (date ? moment(date).format("DD.MM.YYYY") : "");

  // Active documents (no end date) first, then by end date from newest to oldest
  const sortByValidity = (documents = []) =>
    [...documents].sort((a, b) => {
      if (!a.date_active_to !== !b.date_active_to) return a.date_active_to ? 1 : -1;
      return (b.date_active_to || "").localeCompare(a.date_active_to || "") || b.id - a.id;
    });

  // Columns shared by the offers and orders tables in the documents tab
  const getDocumentColumns = (profilePath) => [
    {
      title: t("buildings.documentId"),
      key: "id",
      dataIndex: "id",
      render: (id) => {
        return (
          <Link to={profilePath + id} style={{ borderBottom: "1px dotted" }}>
            {id}
          </Link>
        );
      },
    },
    {
      title: t("offers.summaryDetails.total"),
      key: "price",
      dataIndex: "price",
      render: (text, row, index) => {
        return (
          <Link to={profilePath + row.id} style={{ fontWeight: 500 }}>
            {new Intl.NumberFormat("cs-CZ", {
              style: "currency",
              currency: row.currency_iso_4217,
            }).format(text)}
          </Link>
        );
      },
    },
    {
      title: t("offers.dateIssued"),
      key: "date_issued",
      dataIndex: "date_issued",
      render: renderDocumentDate,
    },
    {
      title: t("buildings.documentValidFrom"),
      key: "date_active_from",
      dataIndex: "date_active_from",
      render: renderDocumentDate,
    },
    {
      title: t("buildings.documentValidTo"),
      key: "date_active_to",
      dataIndex: "date_active_to",
      render: renderDocumentDate,
    },
  ];

  return (
    <React.Fragment key={building?.id}>
      <Row gutter={gutter}>
        <Col span={24}>
          <Card loading={isLoading}>
            <Row gutter={[0, 24]} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              {/* Name of building */}
              <div style={{ flex: "1 1 auto" }}>
                <Typography.Title>
                  {building?.registration_number ? building.registration_number + " - " : ""}
                  {String(building?.reference)}
                </Typography.Title>

                <Space>
                  <Typography.Title level={5} type="secondary" style={{ color: "#1890ff", fontWeight: 400 }}>
                    <Link to={`/clients/${building?.client_id}`}>
                      <Space size={[6, 12]}>
                        <BankOutlined />
                        {building?.client?.name}
                      </Space>
                    </Link>
                  </Typography.Title>
                </Space>
              </div>

              {/* Right side container */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                {/* Active cleanings info */}
                <BuildingUnitsActiveCleanings key={building?.id} buildingUnits={building?.building_units} />

                {/* Actions buttons */}
                <RecordActionsBar
                  redirectState={redirectState}
                  setRedirect={() => {
                    setRedirectState(true);
                  }}
                  firstModalTitle={t("buildings.delete.alertTitle")}
                  firstModalContent={t("buildings.delete.alertContent")}
                  entityRoute="/buildings"
                  recordId={building?.id}
                ></RecordActionsBar>
              </div>
            </Row>

            {/* Tab sections of resource */}
            <CustomTabs props={props} onChange={(e) => { setCurrentTabKey(e) }}>
              <Tabs.TabPane key="#overview" tab={t("buildingUnits.overview")}>
                {building?.id && <BuildingDetails key={building?.id} props={{ ...props }} building={building} setBuilding={setBuilding} />}
              </Tabs.TabPane>

              <Tabs.TabPane key="#units" tab={t("buildings.buildingUnitsOverview")}>
                <BuildingUnitAddForm building={building} setBuilding={setBuilding} />
                <Col span={24}>
                  <SlideDown className={"my-dropdown-slidedown"} closed={selectedRows.length < 1} style={{ overflow: "visible" }}>
                    <Space style={{ marginTop: '20px' }}>
                      <ScheduleMassTerminate selectedBuildingUnits={selectedBuildingUnits} clientNotifyEditable />
                    </Space>
                  </SlideDown>
                </Col>
                <Table
                  size="small"
                  pagination={{
                    showSizeChanger: true,
                  }}
                  style={{ height: "100%", marginTop: "16px" }}
                  loading={isLoading}
                  rowKey={"id"}
                  rowSelection={{
                    selectedRowKeys: selectedRows,
                    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
                    onChange: (selectedRowKeys) => {
                      selectRrules(selectedRowKeys);
                      setSelectedRows(selectedRowKeys);
                    },
                  }}
                  columns={[
                    {
                      title: t("common.address"),
                      dataIndex: "id",
                      sorter: (a, b) => a.house_number - b.house_number,
                      render: (_, row, index) => {
                        const address = `${row.street} ${row.house_number}`;

                        return (
                          <React.Fragment key={row?.id}>
                            <Tooltip placement="topLeft" title={address}>
                              <Link to={"/buildings/" + row.building_id + "/unit/" + row?.id} style={{ fontWeight: 500 }}>
                                {address}
                              </Link>
                            </Tooltip>
                          </React.Fragment>
                        );
                      },
                    },
                    {
                      title: t("cleanings.partner"),
                      key: "partner",
                      render: (row) => {
                        return (
                          <PartnerLink partner={row?.service_rrules?.[row?.service_rrules?.length - 1]?.partner} />
                        );
                      },
                    },
                    {
                      title: t("schedules.cleaningDays"),
                      key: "cleaning_days",
                      dataIndex: "cleaning_days",
                      render: (days, element) => {
                        return showActiveDays(days, element, t);
                      },
                    },
                    {
                      title: t("buildingUnits.validity"),
                      key: "validity",
                      render: (_, row) => {
                        const activeRrule = row?.service_rrules?.[row?.service_rrules?.length - 1];
                        
                        if (!activeRrule || !activeRrule.date_from) {
                          return (
                            <Typography.Text type="warning" style={{ fontStyle: 'italic' }}>
                              {t('buildings.missingCleaningInfo')}
                            </Typography.Text>
                          );
                        }

                        const dateFromFull = activeRrule.date_from ? moment(activeRrule.date_from).format('DD.MM.YYYY') : '';
                        const dateToFull = (activeRrule?.date_to && activeRrule?.date_to !== "null") ? moment(activeRrule.date_to).format('DD.MM.YYYY') : t('common.empty');

                        const dateFromShort = activeRrule.date_from ? activeRrule.date_from.substring(8, 10) + '.' + activeRrule.date_from.substring(5, 7) : '';
                        const dateToShort = (activeRrule?.date_to && activeRrule?.date_to !== "null")
                          ? activeRrule.date_to.substring(8, 10) + '.' + activeRrule.date_to.substring(5, 7)
                          : t('common.empty');

                        return (
                          <Tooltip title={`${dateFromFull} – ${dateToFull}`}>
                            <span>{dateFromShort} – {dateToShort}</span>
                          </Tooltip>
                        );
                      },
                    },
                    {
                      title: t("buildingUnits.city"),
                      dataIndex: "city",
                      sorter: (a, b) => a.city.localeCompare(b.city),
                    },
                    {
                      title: t("buildingUnits.district"),
                      dataIndex: "district",
                      sorter: (a, b) => a.district.localeCompare(b.district),
                      ellipsis: {
                        showTitle: false,
                      },
                      render: (district) => (
                        <Tooltip placement="topLeft" title={district}>
                          {district}
                        </Tooltip>
                      ),
                    },
                    {
                      title: t("schedules.pricePerUnit"),
                      render: () =>
                        new Intl.NumberFormat("cs-CZ", {
                          style: "currency",
                          currency: building?.city_branch?.country_branch?.iso_4217_currency_code || "CZK",
                        }).format(building?.price_per_unit),
                    },
                    {
                      title: t("buildingUnits.flatsCount"),
                      dataIndex: "number_of_flats",
                      sorter: (a, b) => a.number_of_flats - b.number_of_flats,
                    },
                    {
                      title: t("buildingUnits.floorCount"),
                      dataIndex: "floor_count",
                      sorter: (a, b) => a.floor_count - b.floor_count,
                    },
                    {
                      title: t("buildingUnits.groundFloorCount"),
                      dataIndex: "ground_floor_count",
                      sorter: (a, b) => a.ground_floor_count - b.ground_floor_count,
                    },
                    {
                      title: t("buildingUnits.basementFloorCount"),
                      dataIndex: "basement_floor_count",
                      sorter: (a, b) => a.basement_floor_count - b.basement_floor_count,
                    },
                    {
                      title: t("buildingUnits.propertySize"),
                      dataIndex: "property_size",
                      sorter: (a, b) => a.property_size - b.property_size,
                    },
                    {
                      title: t("buildingUnits.propertyType"),
                      dataIndex: "property_type",
                      sorter: (a, b) => a.property_type - b.property_type,
                    },
                  ]}
                  dataSource={building.building_units}
                  expandable={{
                    expandedRowRender: (buildingUnit) => {
                      return (
                        <p style={{ margin: 0 }}>
                          {buildingUnit?.cleaning_service_groups?.map((cleaningServiceGroup) => {
                            return (
                              <React.Fragment key={cleaningServiceGroup?.id}>
                                <strong>{cleaningServiceGroup?.name}</strong>

                                <div style={{ marginBottom: "6px", whiteSpace: "break-spaces" }}>
                                  {cleaningServiceGroup?.cleaning_service_group_contents?.map(
                                    (cleaningServiceGropContent) => (
                                      <em key={cleaningServiceGropContent.id}>{cleaningServiceGropContent.service}</em>
                                    )
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </p>
                      );
                    },
                    expandIcon: ({ expanded, onExpand, record, expandable }) =>
                      expandable && (
                        <Tooltip placement="top" title={t("buildings.showServiceContent")}>
                          <InfoCircleOutlined
                            onClick={(e) => onExpand(record, e)}
                            style={{ transition: "linear 1s 0.2s" }}
                          />
                        </Tooltip>
                      ),
                    rowExpandable: (buildingUnit) => buildingUnit?.cleaning_service_groups?.length > 0,
                  }}
                ></Table>
              </Tabs.TabPane>

              <Tabs.TabPane key="#issuedOffers" tab={t("buildings.issuedOffersAndOrders")}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Divider orientation={"left"}>{t("buildings.issuedOffers")}</Divider>
                    <Table
                      size="small"
                      pagination={{ pageSize: 5, showSizeChanger: true }}
                      loading={isLoading}
                      dataSource={sortByValidity(building.offers)}
                      rowKey={"id"}
                      columns={getDocumentColumns("/offers/")}
                    ></Table>
                  </Col>
                  <Col span={12}>
                    <Divider orientation={"left"}>{t("buildings.ordersOverview")}</Divider>
                    <Table
                      size="small"
                      loading={isLoading}
                      dataSource={sortByValidity(building.orders)}
                      pagination={{ pageSize: 5, showSizeChanger: true }}
                      rowKey={"id"}
                      columns={getDocumentColumns("/orders/")}
                    ></Table>
                  </Col>
                </Row>
              </Tabs.TabPane>

              <Tabs.TabPane key="#invoices" tab={t("buildingUnits.invoices")}>
                <BuildingInvoicePane contacts={building.contacts} />
              </Tabs.TabPane>

              <Tabs.TabPane key="#contacts" tab={t("buildingUnits.contacts")}>
                <Tabs defaultActiveKey="0">
                  <TabPane tab={t("contacts.onBuildings")} key="0">
                    <BuildingUnitContactPane
                      contacts={building.contacts}
                      contactableId={props.match.params.id}
                      contactableType={"building"}
                    />
                  </TabPane>

                  {building?.building_units?.map((buildingUnit, i) => {
                    return (
                      <React.Fragment key={buildingUnit?.id || `building-unit-${i}`}>
                        <TabPane tab={`${buildingUnit?.street} ${buildingUnit?.house_number}`} key={buildingUnit?.id || i + 1}>
                          {buildingUnit?.contacts && (
                            <React.Fragment key={`contacts-${buildingUnit?.id || i}`}>
                              <ContactsPane
                                contacts={buildingUnit?.contacts}
                                contactable={{ id: buildingUnit?.id, type: "buildingUnit" }}
                              />
                            </React.Fragment>
                          )}
                        </TabPane>
                      </React.Fragment>
                    );
                  })}
                </Tabs>
              </Tabs.TabPane>

              <Tabs.TabPane key="#communication" tab={t("buildingUnits.communication")}>
                <CommunicationPane currentTabKey={currentTabKey} building={building} />
              </Tabs.TabPane>
              <Tabs.TabPane tab={t("cleanings.cleanings")} key="#cleanings">
                <CleaningListComplex building={building} currentTabKey={currentTabKey} />
              </Tabs.TabPane>
              <Tabs.TabPane tab={t("buildingUnits.claims")} key="#claims">
                <ClaimsPane building={building} currentTabKey={currentTabKey} />
              </Tabs.TabPane>
            </CustomTabs>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default BuildingProfile;
